import React, { useContext, useEffect, useRef, useState } from 'react';
import './devices.css';
import AIEduDataTable from '../common/DataTable/AIEduDataTable';
import AIEduSearch from '../common/Search/AIEduSearch';
import AIEduPaginator from '../common/Paginator/AIEduPaginator';
import AIEduButton from '../common/Button/AIEduButton';
import AIEduSpinner from '../common/Spinner/AIEduSpinner';
import AIEduToast from '../common/Toast/AIEduToast';
import AddDevice from './AddDevice';
import { API } from '../../services';
import { useAuthenticate } from '../../hooks';
import { Context } from '../../context/Context';
import { setToastComponent } from '../../services/setup/ApiResponseHandler';
import DeleteDeviceDialog from './DeleteDeviceDialog';
import { useIntl } from 'react-intl';
import { formatDateWithTimeStamp } from '../../utils/Utils';
import { ASC, DESC } from '../../utils/Constants';
import PageTitle from '../layout/PageTitle/pageTitle';

const Devices = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {}; // assuming there is only one organization for now. This can be changed later.

    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const {
        refreshMap,
        isAddDeviceVisible,
        setIsAddDeviceVisible,
        sideMenuCurrentActive,
    } = useContext(Context);

    const [searchTerm, setSearchTerm] = useState('');
    const toastRef = useRef(null);
    const [selectedDevicesList, setSelectedDevicesList] = useState([]);
    const [visible, setVisible] = useState(false);
    const [deleteAction, setDeleteAction] = useState(null);
    const [deleteMessage, setDeleteMessage] = useState('');

    const [sortField, setSortField] = useState('createdTime');
    const [sortOrder, setSortOrder] = useState(-1);
    const [tableKey, setTableKey] = useState(false);

    const onPageChange = (e) => {
        const page = e.page;
        const size = e.rows;

        setFirst(e.first);
        setRows(size);
        setPage(page);
        setTableKey(!tableKey);
        setSelectedDevicesList([]);

        getAllScreenDetails(page, size, sortField, sortOrder, searchTerm);
    };

    const getAllScreenDetails = (start, rowsPerPage, sortBy, sort, search) => {
        setLoading(true);
        const params = {
            start,
            rowsPerPage,
            sortBy: sortBy || 'createdTime',
            sort: sort === 1 ? ASC : DESC,
            search,
        };
        API.getScreensDetails(organizationId, placeId, params)
            .then(({ data }) => {
                const { screens, total, sortBy, sort } = data ?? {};
                if (screens?.length === 0 && start > 0 && total > 0) {
                    setFirst(0);
                    setPage(0);
                    getAllScreenDetails(
                        0,
                        rowsPerPage,
                        sortBy,
                        sortOrder,
                        search
                    );
                } else {
                    const devicesList = [];
                    screens?.forEach((screen) => {
                        const { screenId, screenName, createdTime } =
                            screen ?? {};
                        devicesList.push({
                            screenId,
                            createdTime,
                            screenName: screenName ? screenName : '-',
                        });
                    });
                    setDevices(devicesList);
                    setTotalRecords(total);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const resetPageParams = () => {
        setFirst(0);
        setPage(0);
        setSortField('createdTime');
        setSortOrder(-1);
        setSearchTerm('');
        setSelectedDevicesList([]);
    };

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    useEffect(() => {
        if (sideMenuCurrentActive !== 0) {
            setIsAddDeviceVisible(false);
        }

        resetPageParams();
        getAllScreenDetails(0, rows);
    }, [refreshMap.device]);

    const actionBodyTemplate = (rowData) => {
        const { screenId } = rowData ?? {};
        const confirmSingleDelete = () => {
            setDeleteMessage(f({ id: 'AI_EDU_DELETE_DEVICE_TEXT' }));
            setDeleteAction(() => () => {
                handleDeleteDevicesList([screenId]);
            });
            setVisible(true);
        };
        return (
            <i
                className='pi pi-trash cursor-pointer'
                style={{ fontSize: '20px' }}
                onClick={confirmSingleDelete}
            />
        );
    };

    const createdTimeTemplate = (rowData) => {
        return !!rowData.createdTime
            ? formatDateWithTimeStamp(rowData.createdTime)
            : '--';
    };

    const columns = [
        {
            field: 'screenId',
            header: f({ id: 'AI_EDU_DEVICE_MAC' }),
            width: '30%',
            minWidth: '15rem',
            sortable: true,
        },
        {
            field: 'screenName',
            header: f({ id: 'AI_EDU_DEVICE_NAME' }),
            width: '30%',
            minWidth: '20rem',
            sortable: true,
        },
        {
            header: 'Enrolled Date',
            field: 'createdTime',
            width: '20%',
            minWidth: '10rem',
            body: createdTimeTemplate,
            sortable: true,
        },
        {
            header: f({ id: 'AI_EDU_ACTION' }),
            body: actionBodyTemplate,
            minWidth: '5rem',
        },
    ];

    const handleDeleteDevicesList = (deviceList) => {
        API.deleteScreens(organizationId, placeId, deviceList)
            .then((res) => {
                if (res.status === 200) {
                    setSelectedDevicesList((prev) =>
                        prev.filter(
                            (device) => !deviceList.includes(device.screenId)
                        )
                    );
                    if (deviceList === devices) {
                        setPage(Math.max(0, page - 1));
                    }
                    getAllScreenDetails(
                        deviceList === devices ? Math.max(0, page - 1) : page,
                        rows,
                        sortField,
                        sortOrder,
                        searchTerm
                    );
                }
            })
            .finally(() => {
                setVisible(false);
                setDeleteMessage('');
            });
    };

    const navigateToAddDevice = () => {
        setIsAddDeviceVisible(true);
    };

    const handleMultipleDeviceDelete = () => {
        if (selectedDevicesList?.length === 1) {
            setDeleteMessage(f({ id: 'AI_EDU_DELETE_DEVICE_TEXT' }));
        } else {
            setDeleteMessage(f({ id: 'AI_EDU_DELETE_DEVICES_TEXT' }));
        }
        const screens = selectedDevicesList?.map((devices) => devices.screenId);
        setDeleteAction(() => () => handleDeleteDevicesList(screens));
        setVisible(true);
    };

    const handleDeviceSearch = () => {
        setFirst(0);
        setPage(0);
        setSelectedDevicesList([]);
        getAllScreenDetails(0, rows, sortField, sortOrder, searchTerm);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleDeviceSearch();
        }
    };

    const onSort = (e) => {
        const { field, order } = { field: e.sortField, order: e.sortOrder };

        getAllScreenDetails(
            page,
            rows,
            field,
            order,
            !!searchTerm ? searchTerm : null
        );

        setSortField(field);
        setSortOrder(order);
    };

    const sortIcon = (options) => {
        if (options.sortOrder === 1) {
            return (
                <i
                    className='pi pi-sort-up-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
        if (options.sortOrder === -1) {
            return (
                <i
                    className='pi pi-sort-down-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <DeleteDeviceDialog
                visible={visible}
                hideDeleteDevicesDialog={() => setVisible(!visible)}
                deleteSelectedDevices={deleteAction}
                deleteMessage={deleteMessage}
            />
            <AIEduToast ref={toastRef} />
            {!isAddDeviceVisible ? (
                <div className='devices'>
                    <PageTitle
                        title={f({ id: 'AI_EDU_DEVICES' })}
                        count={totalRecords}
                    />
                    <div className='mb-4 flex justify-content-between flex-wrap gap-3'>
                        <AIEduSearch
                            value={searchTerm}
                            setValue={setSearchTerm}
                            handleInputKeyDown={handleInputKeyDown}
                            placeholder={f({ id: 'AI_EDU_SEARCH_DEVICE_MAC' })}
                            searchHandler={handleDeviceSearch}
                        />
                        <div className='flex align-items-center'>
                            <AIEduButton
                                icon='pi pi-trash'
                                className='devices-delete mr-4'
                                onClick={handleMultipleDeviceDelete}
                                disabled={selectedDevicesList.length === 0}
                            />
                            <div className='vertical_divider'></div>
                            <AIEduButton
                                label={f({ id: 'AI_EDU_ADD_DEVICE' })}
                                icon='pi pi-plus'
                                className='btnPrimary'
                                onClick={navigateToAddDevice}
                            />
                        </div>
                    </div>
                    <div
                        className='mb-2'
                        style={{
                            height: '1rem',
                            fontFamily: 'SamsungOne-700, sans-serif',
                        }}
                    >
                        {selectedDevicesList?.length > 0 && (
                            <span>
                                {selectedDevicesList.length} of{' '}
                                {devices?.length} selected
                            </span>
                        )}
                    </div>
                    <AIEduDataTable
                        key={tableKey}
                        data={devices}
                        columns={columns}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        sortIcon={sortIcon}
                        onSort={onSort}
                        lazy={true}
                        dataKey='screenId'
                        selection={selectedDevicesList}
                        onSelectionChange={(e) =>
                            setSelectedDevicesList(e.value)
                        }
                        scrollHeight='60vh'
                        selectionMode='multiple'
                        emptyMessage={f({ id: 'AI_EDU_NO_DATA_TO_SHOW' })}
                    />

                    {devices?.length > 0 && (
                        <AIEduPaginator
                            rows={rows}
                            first={first}
                            totalRecords={totalRecords}
                            onPageChange={onPageChange}
                        />
                    )}
                </div>
            ) : (
                <AddDevice
                    setIsAddDeviceVisible={setIsAddDeviceVisible}
                    setLoading={setLoading}
                />
            )}
        </>
    );
};

export default Devices;
